import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import { FaEllipsisV } from "react-icons/fa";
import DeleteModal from "../modal/DeleteModal";
import ViewUserModal from "../modal/ViewUserModal";
import { request } from "../../../utils/api";
import { toast, ToastContainer } from "react-toastify";
import Loading from "../utilComponent/Loading";
import Pagination from "../utilComponent/Pagination";
import { formatYYYYMMDD } from "../../../utils";

const PackageList = () => {
  const navigate = useNavigate();

  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
  });

  const getPackages = async (page = 1, search = searchText) => {
    try {
      setLoading(true);
      const res = await request({
        url: `packages?page=${page}&per_page=${pagination.per_page}&search=${search}`,
        method: "GET",
      });

      if (res?.status && res?.data) {
        setPackages(
          res.data.items.map((item) => ({
            id: item.id,
            title: item.title,
            price: item.price,
            description: item.description,
            service: item.service?.title || "-",
            created_at: formatYYYYMMDD(item.created_at),
          }))
        );
        setPagination(res.data.pagination);
      }
    } catch {
      toast.error("Failed to fetch packages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getPackages(1);
  }, []);

  const deletePackage = async () => {
    try {
      setLoading(true);
      await request({
        url: `packages/${selectedPackage.id}`,
        method: "DELETE",
      });

      toast.success("Package deleted successfully");
      setShowDeleteModal(false);
      setSelectedPackage(null);
      getPackages(pagination.current_page);
    } catch {
      toast.error("Cannot delete package");
    } finally {
      setLoading(false);
    }
  };

  const packageFields = [
    { label: "Service", key: "service" },
    { label: "Package Title", key: "title" },
    { label: "Price", key: "price" },
    { label: "Description", key: "description", isHtml: true },
    { label: "Created Date", key: "created_at" },
  ];

  return (
    <div>
      <ToastContainer/>
      <ViewUserModal
        show={showViewModal}
        onClose={() => setShowViewModal(false)}
        data={selectedPackage}
        fields={packageFields}
        title="Package Details"
        onEdit={(item) => navigate(`/package-update/${item.id}`)}
      />

      <DeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={deletePackage}
      />

      {/* Breadcrumb */}
      <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
        <div className="container-fluid">
          <ol className="breadcrumb bg-transparent mb-0">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item active">Packages</li>
            <li className="breadcrumb-item active">Package List</li>
          </ol>
        </div>
      </div>

      {/* Content */}
      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1">
        <div className="container-fluid">
          <div className="card shadow-sm">
            <div className="card-header">
              <h6 className="card-title m-0">Package List</h6>
            </div>

            <div className="card-body">
              {/* Search */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <input
                    className="form-control"
                    placeholder="Search packages..."
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value);
                      getPackages(1, e.target.value);
                    }}
                  />
                </div>
              </div>

              {loading ? (
                <Loading />
              ) : (
                <div className="row">
                  {packages.length ? (
                    packages.map((item) => (
                      <div key={item.id} className="col-xl-4 col-lg-6 mb-4">
                        <div className="card h-100 shadow-sm">
                          <div className="card-body">
                            <div className="d-flex justify-content-between mb-2">
                              <h6 className="fw-bold">{item.title}</h6>

                              <Dropdown align="end">
                                <Dropdown.Toggle
                                  variant="light"
                                  className="border-0 p-0 shadow-none"
                                >
                                  <FaEllipsisV />
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                  <Dropdown.Item
                                    onClick={() => {
                                      setSelectedPackage(item);
                                      setShowViewModal(true);
                                    }}
                                  >
                                    View
                                  </Dropdown.Item>

                                  <Dropdown.Item
                                    onClick={() =>
                                      navigate(`/package-update/${item.id}`)
                                    }
                                  >
                                    Edit
                                  </Dropdown.Item>

                                  <Dropdown.Divider />

                                  <Dropdown.Item
                                    className="text-danger"
                                    onClick={() => {
                                      setSelectedPackage(item);
                                      setShowDeleteModal(true);
                                    }}
                                  >
                                    Delete
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>

                            <p className="mb-1">
                              🛠 <strong>Service:</strong> {item.service}
                            </p>

                            <p className="mb-1">
                              💰 <strong>Price:</strong> ${item.price}
                            </p>

                            <div
                              className="text-muted mb-2"
                              style={{ maxHeight: "60px", overflow: "hidden" }}
                              dangerouslySetInnerHTML={{
                                __html: item.description,
                              }}
                            />

                            <p className="mb-0">
                              📅 <strong>Created:</strong> {item.created_at}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-5">No packages found</div>
                  )}
                </div>
              )}

              {pagination.last_page > 1 && (
                <Pagination
                  currentPage={pagination.current_page}
                  lastPage={pagination.last_page}
                  onPageChange={(page) => getPackages(page)}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackageList;
